import { GameState, Role } from "./types";

export interface DebugController {
  enabled: boolean;
  updateState: (state: GameState) => void;
  setPaused: (paused: boolean) => void;
  setRecentEvents: (events: string[]) => void;
  setControlsEnabled: (enabled: boolean) => void;
}

interface DebugElements {
  panel: HTMLDivElement;
  pauseButton: HTMLButtonElement;
  phaseLabel: HTMLSpanElement;
  roundLabel: HTMLSpanElement;
  livingLabel: HTMLSpanElement;
  mafiaLabel: HTMLSpanElement;
  eventsList: HTMLUListElement;
  statusLabel: HTMLDivElement;
}

interface DebugOptions {
  onTogglePause: (shouldPause: boolean) => void;
}

const NOOP_CONTROLLER: DebugController = {
  enabled: false,
  updateState: () => {},
  setPaused: () => {},
  setRecentEvents: () => {},
  setControlsEnabled: () => {},
};

export function initDebugMode(options: DebugOptions): DebugController {
  const enabled = localStorage.getItem("debug_mode") === "true";
  if (!enabled) {
    return NOOP_CONTROLLER;
  }

  const elements = createDebugPanel(options.onTogglePause);
  let currentPaused = false;

  const controller: DebugController = {
    enabled: true,
    updateState: (state: GameState) => {
      elements.phaseLabel.textContent = formatPhase(state.phase);
      elements.roundLabel.textContent = state.day.toString();
      const livingPlayers = state.players.filter((player) => player.isAlive);
      const livingMafia = livingPlayers.filter(
        (player) => player.role === Role.MAFIA
      );
      elements.livingLabel.textContent = livingPlayers.length.toString();
      elements.mafiaLabel.textContent = livingMafia.length.toString();
      elements.statusLabel.textContent = state.gameId
        ? `Game ID: ${state.gameId}`
        : "Game not started";
    },
    setPaused: (paused: boolean) => {
      currentPaused = paused;
      elements.pauseButton.textContent = paused ? "⏯️  RESUME" : "⏸️  PAUSE";
      elements.pauseButton.dataset.state = paused ? "paused" : "running";
    },
    setRecentEvents: (events: string[]) => {
      elements.eventsList.replaceChildren();
      if (events.length === 0) {
        const emptyItem = document.createElement("li");
        emptyItem.textContent = "No recent events.";
        emptyItem.style.opacity = "0.6";
        elements.eventsList.appendChild(emptyItem);
        return;
      }

      for (const eventText of events) {
        const item = document.createElement("li");
        item.textContent = eventText;
        elements.eventsList.appendChild(item);
      }
    },
    setControlsEnabled: (isEnabled: boolean) => {
      elements.pauseButton.disabled = !isEnabled;
      elements.pauseButton.classList.toggle("disabled", !isEnabled);
    },
  };

  // initialise default state
  controller.setPaused(currentPaused);
  controller.setRecentEvents([]);
  controller.setControlsEnabled(false);

  return controller;
}

function createDebugPanel(onTogglePause: (shouldPause: boolean) => void): DebugElements {
  const panel = document.createElement("div");
  panel.id = "debug-panel";
  panel.style.cssText = `
    position: fixed;
    top: 96px;
    right: 24px;
    width: 340px;
    max-height: 70vh;
    background: rgba(10, 10, 10, 0.92);
    border: 1px solid #00ff88;
    border-radius: 12px;
    padding: 16px;
    font-family: "JetBrains Mono", "Fira Code", monospace;
    font-size: 0.85rem;
    color: #c8ffee;
    overflow-y: auto;
    z-index: 1200;
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.45);
  `;

  const closeButton = document.createElement("button");
  closeButton.textContent = "✕";
  closeButton.style.cssText = `
    position: absolute;
    top: 8px;
    right: 8px;
    background: transparent;
    border: none;
    color: #00ff88;
    cursor: pointer;
    font-size: 1.1rem;
  `;
  closeButton.title = "Hide debug panel";
  closeButton.addEventListener("click", () => {
    panel.remove();
    localStorage.removeItem("debug_mode");
  });
  panel.appendChild(closeButton);

  const title = document.createElement("div");
  title.textContent = "🐛 DEBUG MODE";
  title.style.fontWeight = "bold";
  title.style.marginBottom = "12px";
  title.style.letterSpacing = "0.04em";
  panel.appendChild(title);

  const statusLabel = document.createElement("div");
  statusLabel.id = "debug-status-text";
  statusLabel.style.marginBottom = "12px";
  statusLabel.style.opacity = "0.85";
  panel.appendChild(statusLabel);

  const pauseButton = document.createElement("button");
  pauseButton.id = "debug-pause-btn";
  pauseButton.textContent = "⏸️  PAUSE";
  pauseButton.style.cssText = `
    width: 100%;
    padding: 10px;
    margin-bottom: 16px;
    background: linear-gradient(135deg, #00ff88 0%, #00bfa5 100%);
    color: #04100c;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 600;
    letter-spacing: 0.03em;
    transition: transform 0.12s ease, box-shadow 0.12s ease, opacity 0.2s ease;
  `;
  pauseButton.addEventListener("mouseenter", () => {
    if (!pauseButton.disabled) {
      pauseButton.style.transform = "translateY(-1px)";
      pauseButton.style.boxShadow = "0 10px 20px rgba(0, 255, 136, 0.25)";
    }
  });
  pauseButton.addEventListener("mouseleave", () => {
    pauseButton.style.transform = "";
    pauseButton.style.boxShadow = "";
  });
  pauseButton.addEventListener("click", () => {
    if (pauseButton.disabled) {
      return;
    }
    const desiredState = pauseButton.dataset.state !== "paused";
    onTogglePause(desiredState);
  });
  panel.appendChild(pauseButton);

  const infoContainer = document.createElement("div");
  infoContainer.style.display = "grid";
  infoContainer.style.gridTemplateColumns = "repeat(2, 1fr)";
  infoContainer.style.gap = "6px 12px";
  infoContainer.style.marginBottom = "16px";

  const phaseLabel = createInfoRow(infoContainer, "Phase");
  const roundLabel = createInfoRow(infoContainer, "Round");
  const livingLabel = createInfoRow(infoContainer, "Living");
  const mafiaLabel = createInfoRow(infoContainer, "Mafia");

  panel.appendChild(infoContainer);

  const eventsHeader = document.createElement("div");
  eventsHeader.textContent = "Recent events";
  eventsHeader.style.marginBottom = "6px";
  eventsHeader.style.fontWeight = "600";
  panel.appendChild(eventsHeader);

  const eventsList = document.createElement("ul");
  eventsList.style.listStyle = "none";
  eventsList.style.margin = "0";
  eventsList.style.padding = "0";
  eventsList.style.display = "flex";
  eventsList.style.flexDirection = "column";
  eventsList.style.gap = "4px";
  panel.appendChild(eventsList);

  document.body.appendChild(panel);

  return {
    panel,
    pauseButton,
    phaseLabel,
    roundLabel,
    livingLabel,
    mafiaLabel,
    eventsList,
    statusLabel,
  };
}

function createInfoRow(container: HTMLDivElement, label: string): HTMLSpanElement {
  const row = document.createElement("div");
  row.style.display = "flex";
  row.style.flexDirection = "column";
  row.style.gap = "2px";

  const labelElement = document.createElement("span");
  labelElement.textContent = label;
  labelElement.style.opacity = "0.7";
  labelElement.style.fontSize = "0.75rem";

  const valueElement = document.createElement("span");
  valueElement.textContent = "—";
  valueElement.style.fontSize = "0.95rem";
  valueElement.style.fontWeight = "600";

  row.appendChild(labelElement);
  row.appendChild(valueElement);
  container.appendChild(row);
  return valueElement;
}

function formatPhase(phase: GameState["phase"]): string {
  if (typeof phase === "string") {
    return phase.charAt(0).toUpperCase() + phase.slice(1).toLowerCase();
  }
  return phase;
}
